/**
 * One entity-type card in the Research browse grid.
 *
 * Visual spec: storybook `Prototypes/ModernDesign/Pages/Research`. The
 * production version reads colors from `LABEL_COLORS` and icons from
 * `ENTITY_ICON_COMPONENTS` (canonical mappings shared with the graph
 * canvas) so type identity carries between browse → graph → drill-in.
 *
 * Click targets:
 *   - Header (icon + label + count + chevron) → drill in to the per-type list
 *   - Body row → drill in to the entity (TODO: wire entity detail navigation)
 *   - Footer "Show all N" → same as header (only shown when total > preview)
 */
import { ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ENTITY_ICON_COMPONENTS, LABEL_COLORS } from '@/components/neo4j/cytoscape/entity-icons'
import { Card } from '@/components/ui/card'
import {
  type EntityGroupPreview,
  type EntityHit,
  getEntityHitDisplayName,
} from '@/services/api/case-search'
import { cn } from '@/utils/cn'
import { HighlightedText } from './HighlightedText'

interface Props {
  caseId: string
  group: EntityGroupPreview
  /** When set, rows highlight matched substrings in the entity name/subtitle. */
  query?: string
  /** Fired when a preview row is clicked. Opens the entity detail sheet. */
  onSelectEntity?: (hit: EntityHit) => void
  /** Rows do not name their source document: the page is filtered to one. */
  hideSource?: boolean
}

export function EntityGroupCard({ caseId, group, query, onSelectEntity, hideSource }: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const color = LABEL_COLORS[group.type] ?? LABEL_COLORS.Default
  const Icon = ENTITY_ICON_COMPONENTS[group.type] ?? ENTITY_ICON_COMPONENTS.Default
  const showAllHref = `/cases/${caseId}/search/entities/${encodeURIComponent(group.type)}`
  const drillIn = () => navigate(showAllHref)
  const label = t(`entityType.${group.type}`, { defaultValue: group.type })
  const moreCount = group.total - group.preview.length

  return (
    <Card className="group/card flex flex-col overflow-hidden p-0 transition-colors hover:border-border/80">
      {/* Header is the primary "show all" click target. */}
      <button
        type="button"
        onClick={drillIn}
        className={cn(
          'w-full items-center gap-x-3 gap-y-0.5 border-b border-border/60 px-3.5 py-3 text-left transition-colors hover:bg-muted/30',
          // Search mode below md: the long "N match · M total" count sits under
          // the label instead of squeezing it into a mid-word break. In a flex
          // row the col-/row- placement classes of the children do nothing.
          query ? 'grid grid-cols-[auto_minmax(0,1fr)_auto] md:flex' : 'flex'
        )}
      >
        <span
          className="row-span-2 flex size-7 shrink-0 items-center justify-center rounded-md"
          style={{ backgroundColor: `${color.fill}1a`, color: color.fill }}
        >
          <Icon className="size-3.5" />
        </span>
        <span className="min-w-0 flex-1 text-sm font-medium text-foreground max-md:line-clamp-2 max-md:break-words md:truncate">
          {label}
        </span>
        <span className="col-start-2 row-start-2 shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
          {query
            ? t('research.card.matchCount', { matches: group.preview.length, total: group.total })
            : group.total.toLocaleString()}
        </span>
        <ChevronRight className="col-start-3 row-span-2 row-start-1 size-3.5 shrink-0 text-muted-foreground/50 transition-colors group-hover/card:text-muted-foreground" />
      </button>

      <ul className="divide-y divide-border/40">
        {group.preview.map(hit => (
          <EntityRow
            key={hit.entity_id}
            hit={hit}
            color={color.fill}
            query={query}
            onSelect={onSelectEntity ? () => onSelectEntity(hit) : undefined}
            hideSource={hideSource}
          />
        ))}
      </ul>

      {!query && moreCount > 0 && (
        <button
          type="button"
          onClick={drillIn}
          className="border-t border-border/40 bg-muted/10 px-3.5 py-3 text-left text-2xs font-medium md:py-2 text-muted-foreground/80 transition-colors hover:bg-muted/30 hover:text-foreground"
        >
          {t('research.card.showAllN', { total: group.total.toLocaleString() })}
          <span className="ml-1 text-muted-foreground/50">+{moreCount.toLocaleString()}</span>
        </button>
      )}
    </Card>
  )
}

function EntityRow({
  hit,
  color,
  query,
  onSelect,
  hideSource,
}: {
  hit: EntityHit
  color: string
  query?: string
  onSelect?: () => void
  hideSource?: boolean
}) {
  const primary = getEntityHitDisplayName(hit)
  const subtitle = hideSource ? null : hit.document_name
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        disabled={!onSelect}
        className="flex w-full items-center gap-2.5 px-3.5 py-2 text-left transition-colors hover:bg-muted/30 disabled:cursor-default disabled:hover:bg-transparent md:py-1.5"
      >
        <span
          className="h-1 w-1 shrink-0 rounded-full"
          style={{ backgroundColor: color }}
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm text-foreground">
            <HighlightedText text={primary} query={query} />
          </div>
          {subtitle && (
            <div className="truncate text-[10.5px] text-muted-foreground/80">
              <HighlightedText text={subtitle} query={query} />
            </div>
          )}
        </div>
      </button>
    </li>
  )
}
